import React, { useEffect, useState } from "react";
import { Table, Pagination } from "react-bootstrap";
import { FaBell } from "react-icons/fa";
import { HiOutlineChevronLeft } from "react-icons/hi";
import { HiChevronRight } from "react-icons/hi2";

const API_URL = process.env.REACT_APP_API_URL;

function NotificationsLists() {
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [leads, setLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState({ type: "" });
  const [type, setType] = useState("");

  const token = localStorage.getItem("token");
  const pageLimit = 10;

  const getPaginationGroup = () => {
    let pages = [];
    const totalPagesToShow = 7;
    const sidePages = 2;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    let startPage = Math.max(2, currentPage - sidePages);
    let endPage = Math.min(totalPages - 1, currentPage + sidePages);

    pages.push(1);

    if (startPage > 2) {
      pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages - 1) {
      pages.push("...");
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const fetchLeads = async (page = 1) => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page,
        limit: pageLimit,
        type,
      }).toString();

      const response = await fetch(`${API_URL}/notification-data?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();

      setLeads(result.data || result.notifications || result.leads || []);
      const total = result.total || result.totalRecords || result.count || 0;
      setTotalRecords(total);
      const calculatedTotalPages = Math.ceil(total / pageLimit);
      setTotalPages(calculatedTotalPages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    fetchLeads(pageNumber);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchLeads(1);
  };

  const toSentenceCase = (text) => {
    if (!text) return "-";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  function formatWord(word) {
    if (!word) return "";
    const spaced = word.replace(/([a-z])([A-Z])/g, "$1 $2");
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
  }

  useEffect(() => {
    fetchLeads(1);
  }, []);

  function formatLabel(label) {
    if (!label) return "-";
    return label
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  const getStartingSerialNumber = () => (currentPage - 1) * pageLimit + 1;
  const getEndingSerialNumber = () =>
    Math.min(currentPage * pageLimit, totalRecords);

  // ═══════════ INLINE STYLES ═══════════
  const isMobile = window.innerWidth <= 768;

  const styles = {
    page: {
      padding: "16px",
      paddingTop: isMobile ? "0" : "16px",
      background: "#f1f5f9",
      minHeight: "calc(100vh - 110px)",
      fontFamily: '"Poppins", "Inter", sans-serif',
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    // ═══ PURPLE GRADIENT HEADER ═══
    headerCard: {
      background: "linear-gradient(135deg, #1e1b4b 0%, #4c1d95 100%)",
      borderRadius: "16px",
      padding: "18px 20px",
      color: "#ffffff",
      boxShadow: "0 10px 30px rgba(30, 27, 75, 0.35)",
    },
    headerRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: "14px",
    },
    headerIcon: {
      width: "46px",
      height: "46px",
      borderRadius: "12px",
      background: "rgba(255,255,255,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "20px",
      flexShrink: 0,
      border: "1px solid rgba(255,255,255,0.2)",
    },
    title: {
      fontSize: "18px",
      fontWeight: 700,
      margin: 0,
      color: "#ffffff",
      letterSpacing: "-0.01em",
    },
    subtitle: {
      fontSize: "12.5px",
      color: "rgba(255,255,255,0.85)",
      margin: 0,
    },
    tableCard: {
      background: "#ffffff",
      borderRadius: "16px",
      boxShadow: "0 4px 20px rgba(15,23,42,0.06)",
      overflow: "hidden",
    },
    tableWrap: {
      width: "100%",
      overflowX: "auto",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "900px",
      marginBottom: 0,
    },
    // ═══ PURPLE GRADIENT TABLE HEADER ═══
    thead: {
      background: "linear-gradient(135deg, #1e1b4b 0%, #4c1d95 100%)",
    },
    th: {
      padding: "12px 14px",
      fontSize: "11.5px",
      fontWeight: 700,
      color: "#ffffff",
      textAlign: "left",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      whiteSpace: "nowrap",
      border: "none",
    },
    td: {
      padding: "12px 14px",
      fontSize: "13px",
      color: "#1a1a2e",
      fontWeight: 500,
      verticalAlign: "middle",
      borderBottom: "1px solid #f1f5f9",
    },
    tdMessage: {
      maxWidth: "350px",
      wordBreak: "break-word",
      whiteSpace: "normal",
      color: "#475569",
      fontSize: "12.5px",
      lineHeight: 1.5,
    },
    tdRemark: {
      maxWidth: "220px",
      wordBreak: "break-word",
      whiteSpace: "normal",
      color: "#475569",
      fontSize: "12.5px",
      lineHeight: 1.5,
    },
    loading: {
      padding: "60px 20px",
      textAlign: "center",
      color: "#64748b",
      fontSize: "14px",
      fontWeight: 500,
    },
    emptyState: {
      padding: "60px 20px",
      textAlign: "center",
      color: "#64748b",
    },
    paginationWrap: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      padding: "16px 20px",
      borderTop: "1px solid #f1f5f9",
      flexWrap: "wrap",
    },
    paginationInfo: {
      fontSize: "12.5px",
      color: "#64748b",
      fontWeight: 500,
      margin: 0,
    },
    pagination: {
      display: "flex",
      gap: "6px",
      flexWrap: "wrap",
      alignItems: "center",
      marginBottom: 0,
      justifyContent: "center",
    },
    pageBtn: (active) => ({
      minWidth: "36px",
      height: "36px",
      padding: "0 10px",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      background: active ? "#4c1d95" : "#ffffff",
      color: active ? "#ffffff" : "#475569",
      fontSize: "12.5px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
      fontFamily: "inherit",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    }),
    pageBtnDisabled: {
      minWidth: "36px",
      height: "36px",
      padding: "0 10px",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      background: "#ffffff",
      color: "#cbd5e1",
      fontSize: "12.5px",
      fontWeight: 600,
      cursor: "not-allowed",
      fontFamily: "inherit",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    },
  };

  return (
    <div style={styles.page}>
      {/* ═══════════ HEADER CARD ═══════════ */}
      <div style={styles.headerCard}>
        <div style={styles.headerRow}>
          <div style={styles.headerLeft}>
            <div style={styles.headerIcon}>
              <FaBell />
            </div>
            <div>
              <h2 style={styles.title}>Notifications List</h2>
              <p style={styles.subtitle}>
                All your recent notifications and updates
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ TABLE CARD ═══════════ */}
      <div style={styles.tableCard}>
        {loading ? (
          <div style={styles.loading}>Loading notifications...</div>
        ) : leads.length === 0 ? (
          <div style={styles.emptyState}>
            <FaBell size={32} style={{ color: "#cbd5e1", marginBottom: 12 }} />
            <h6 style={{ color: "#64748b", margin: 0 }}>No notification found</h6>
          </div>
        ) : (
          <>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead style={styles.thead}>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Lead ID</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Action By</th>
                    <th style={styles.th}>Date Time</th>
                    <th style={styles.th}>Message</th>
                    <th style={styles.th}>Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, index) => (
                    <tr key={lead.id || lead._id || index}>
                      <td style={styles.td}>
                        {getStartingSerialNumber() + index}
                      </td>
                      <td style={styles.td}>
                        {lead.order_id || lead.leadId || "-"}
                      </td>
                      <td style={styles.td}>
                        {formatLabel(lead.type) || "-"}
                      </td>
                      <td style={styles.td}>
                        {formatWord(lead.statusremark || lead.status) || "-"}
                      </td>
                      <td style={styles.td}>
                        {toSentenceCase(lead.action_by || lead.actionBy) || "-"}
                      </td>
                      <td style={styles.td}>
                        {(() => {
                          if (!lead.date_time && !lead.dateTime) return "-";
                          try {
                            const dateString =
                              lead.date_time || lead.dateTime || lead.createdAt;
                            const date = new Date(dateString.replace(" ", "T"));
                            if (isNaN(date.getTime())) return "-";
                            return (
                              date
                                .toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                })
                                .replace(/\//g, "-") +
                              " " +
                              date.toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })
                            );
                          } catch (error) {
                            return "-";
                          }
                        })()}
                      </td>
                      <td style={{ ...styles.td, ...styles.tdMessage }}>
                        {lead.message || "-"}
                      </td>
                      <td style={{ ...styles.td, ...styles.tdRemark }}>
                        {formatWord(lead.remark) || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ═══════════ PAGINATION ═══════════ */}
            <div style={styles.paginationWrap}>
              <p style={styles.paginationInfo}>
                Showing {getStartingSerialNumber()} to {getEndingSerialNumber()} of{" "}
                {totalRecords} entries
              </p>

              <div style={styles.pagination}>
                <button
                  style={
                    currentPage === 1
                      ? styles.pageBtnDisabled
                      : styles.pageBtn(false)
                  }
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <HiOutlineChevronLeft />
                </button>

                {getPaginationGroup().map((item, index) =>
                  item === "..." ? (
                    <span
                      key={`ellipsis-${index}`}
                      style={{
                        padding: "0 8px",
                        color: "#94a3b8",
                        fontWeight: 600,
                      }}
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={index}
                      style={styles.pageBtn(item === currentPage)}
                      onClick={() => handlePageChange(item)}
                    >
                      {item}
                    </button>
                  )
                )}

                <button
                  style={
                    currentPage === totalPages
                      ? styles.pageBtnDisabled
                      : styles.pageBtn(false)
                  }
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <HiChevronRight />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default NotificationsLists;